import AppContext from "PFApp/app_context";
import roundToDecimals from "PFCore/helpers/round_to_decimals";
import useInsightsApi from "PFCore/helpers/use_insights_api";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import BoxRenderer from "../common/box_renderer";
import RolesDemandPreview from "../previews/roles_demand_preview";

type AverageTimeData = {
  average_time: number;
  period: string;
  delta: number;
};

type AverageTime = "created_at" | "shortlisted_at" | "accepted_at" | "filled_at";

type GraphData = {
  num_roles: {
    created: number;
    shortlist: number;
    filled: number;
    expired: number;
    passed_due: number;
    filled_passed_due: number;
  };
  avg_times: Record<AverageTime, AverageTimeData>;
};

const RolesDemandSection = (): JSX.Element => {
  const {
    store: {
      pages: {
        insights: { selectedFilters }
      }
    }
  } = useContext(AppContext);

  const [graphData, setGraphData] = useState<GraphData | undefined>(undefined);
  const { insightsApi, insightsApiError } = useInsightsApi();
  const { t } = useTranslation("insights");

  const getPeriodLabel = (period: string) =>
    (({
      days: "translation:day",
      minutes: "translation:minute"
    }[period] || period) as unknown as TemplateStringsArray);

  useEffect(() => {
    insightsApi("/roles", selectedFilters).then((res) => setGraphData(res));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  if (insightsApiError) {
    return <RolesDemandPreview content={t("noData")} />;
  }

  if (!graphData) {
    return <RolesDemandPreview />;
  }

  const { num_roles, avg_times } = graphData;

  return (
    <div>
      <BoxRenderer
        boxItem={[
          {
            title: t("createdRoles"),
            value: roundToDecimals(num_roles.created),
            tooltip: t("totalCreatedRoles"),
            big: true
          },
          {
            title: t("shortlisted"),
            value: roundToDecimals(num_roles.shortlist),
            tooltip: t("rolesShortlisted"),
            big: true
          },
          {
            title: t("filled"),
            value: roundToDecimals(num_roles.filled),
            tooltip: t("totalRolesFilled"),
            big: true
          }
        ]}
      />
      <BoxRenderer
        boxItem={[
          {
            title: t("expired"),
            value: roundToDecimals(num_roles.expired),
            tooltip: t("totalExpiredRoles"),
            big: true
          },
          {
            title: t("pastDue"),
            value: roundToDecimals(num_roles.passed_due),
            tooltip: t("totalPassedDue"),
            big: true
          },
          {
            title: t("filledPassedDue"),
            value: roundToDecimals(num_roles.filled_passed_due),
            tooltip: t("totalFilledPassedDue"),
            big: true
          }
        ]}
      />
      <BoxRenderer
        boxItem={[
          {
            title: t("avgTimeToShortlist"),
            value: t(getPeriodLabel(avg_times.shortlisted_at.period), {
              count: Math.ceil(avg_times.shortlisted_at.average_time)
            }),
            delta: avg_times.shortlisted_at.delta
          },
          {
            title: t("avgTimeToAccept"),
            value: t(getPeriodLabel(avg_times.shortlisted_at.period), {
              count: Math.ceil(avg_times.accepted_at.average_time)
            }),
            delta: avg_times.accepted_at.delta
          },
          {
            title: t("avgTimeToFill"),
            value: t(getPeriodLabel(avg_times.shortlisted_at.period), {
              count: Math.ceil(avg_times.filled_at.average_time)
            }),
            delta: avg_times.filled_at.delta
          }
        ]}
      />
    </div>
  );
};

export default RolesDemandSection;
